import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Link,
  Tooltip,
} from '@mui/material';
import { formatCurrency, formatDuration } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CalendarMonth as CalendarIcon,
  CameraAlt as CameraIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ProfessionalAvatar from '../components/ProfessionalAvatar';
import professionalsService from '../services/professionalsService';
import photoService from '../services/photoService';
import api from '../services/api';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ContextualHint from '../components/ContextualHint';
import ProfessionalForm from '../components/forms/ProfessionalForm';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { getInitials } from '../utils/professionalNameUtils';
import { ProfessionalsDataProvider, useProfessionalsData } from '../contexts/ProfessionalsDataContext';
import { useLocation } from 'react-router-dom';
import SmartWizard from '../components/wizard/SmartWizard';
import { cardSectionHeader, cardSectionHeaderText, pageHeader } from '../styles/pageStyles';
import { getIconComponent, getIconClass } from '../utils/iconUtils';

export default function ProfessionalsPage() {
  const location = useLocation();
  const [normalizedData, setNormalizedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar} = useSnackbar();
  const [showSmartWizard, setShowSmartWizard] = useState(false);

  // ✅ Estado para modal de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  const [appointmentNextSlots, setAppointmentNextSlots] = useState([]);
  const [loadingNextSlots, setLoadingNextSlots] = useState({});
  
  const { getIcon, getLabel } = useThemeCustom();
  
  // Estado para foto
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  // Cache de timestamps de fotos (centralizado no photoService)

  // Obter dica contextual
  const professionals = normalizedData?.professionals || [];
  const hint = useContextualHint('professionals', { items: professionals });

  // Carregar dados normalizados
  useEffect(() => {
    loadData();
  }, []);

  // Abrir criação via querystring (?create=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowSmartWizard(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location.pathname, location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalsService.getAll();
      
      // Adicionar photoTimestamps do localStorage aos profissionais
      const professionalsWithTimestamps = data.professionals.map(p => ({
        ...p,
        photoTimestamp: photoService.getPhotoTimestamp(p.id)
      }));
      
      setNormalizedData({
        ...data,
        professionals: professionalsWithTimestamps
      });
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handlers CRUD
  const handleCreate = async (data) => {
    try {
      await professionalsService.create(data);
      formDialog.handleClose();
      showSuccess('Profissional criado com sucesso!');
      loadData(); // Recarregar dados normalizados
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await professionalsService.update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Profissional atualizado com sucesso!');
      loadData(); // Recarregar dados normalizados
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ✅ Handlers de Agendamento
  const handleOpenAppointmentDialog = (professionalId, serviceId = null, placeId = null, nextSlots = []) => {
    setAppointmentPrefillConfig({
      professionalId,
      serviceId,
      placeId,
      nextSlots
    });
    setAppointmentNextSlots(nextSlots);
    setAppointmentDialogOpen(true);
  };

  const handleCloseAppointmentDialog = () => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
    setAppointmentNextSlots([]);
  };

  const handleAppointmentSuccess = () => {
    handleCloseAppointmentDialog();
    showSuccess('Agendamento criado com sucesso!');
  };

  const handleSearchNextSlots = async (professionalId, serviceId, placeId) => {
    const key = `${professionalId}-${serviceId}-${placeId}`;
    setLoadingNextSlots(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await api.get('/appointments/next-available-slots', {
        params: { professionalId, serviceId, placeId, count: 3 }
      });
      
      const slots = response.data || [];
      
      if (slots.length === 0) {
        showError('Nenhum horário disponível nos próximos 30 dias');
        return;
      }
      
      // Abrir modal com os slots encontrados
      handleOpenAppointmentDialog(professionalId, serviceId, placeId, slots);
    } catch (err) {
      console.error('Erro ao buscar próximos horários:', err);
      showError('Erro ao buscar próximos horários disponíveis');
    } finally {
      setLoadingNextSlots(prev => ({ ...prev, [key]: false }));
    }
  };

  // ✅ Handlers de Foto
  const handlePhotoClick = (professional) => {
    setSelectedProfessional(professional);
    photoDialog.handleOpen();
  };

  const handlePhotoUpload = async (file) => {
    try {
      if (!selectedProfessional?.id) {
        showError('Profissional não selecionado');
        return;
      }
      await photoService.uploadProfilePhoto(file, selectedProfessional.id);
      
      // Salvar timestamp no localStorage para persistência
      const timestamp = Date.now();
      photoService.savePhotoTimestamp(selectedProfessional.id, timestamp);
      
      // Atualizar timestamp da foto no estado local para forçar reload da imagem (cache busting)
      setNormalizedData(prev => ({
        ...prev,
        professionals: prev.professionals.map(p => 
          p.id === selectedProfessional.id 
            ? { ...p, photoTimestamp: timestamp }
            : p
        )
      }));
      
      photoDialog.handleClose();
      showSuccess('Foto atualizada com sucesso!');
      
      // Recarregar profissionais após upload
      await loadData();
    } catch (err) {
      showError('Erro ao fazer upload: ' + (err.response?.data?.message || err.message));
    }
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando profissionais..." />;
  if (error) return <ErrorState error={error} onRetry={loadData} />;
  if (!normalizedData) return null;

  return (
    <ProfessionalsDataProvider data={normalizedData}>
      <ProfessionalsPageContent
        professionals={normalizedData.professionals}
        places={normalizedData.places || []}
        services={normalizedData.services || []}
        hint={hint}
        formDialog={formDialog}
        deleteDialog={deleteDialog}
        photoDialog={photoDialog}
        selectedProfessional={selectedProfessional}
        snackbar={snackbar}
        showSuccess={showSuccess}
        hideSnackbar={hideSnackbar}
        loadData={loadData}
        handleSubmit={handleSubmit}
        handlePhotoClick={handlePhotoClick}
        handlePhotoUpload={handlePhotoUpload}
        handleOpenAppointmentDialog={handleOpenAppointmentDialog}
        handleSearchNextSlots={handleSearchNextSlots}
        loadingNextSlots={loadingNextSlots}
        getLabel={getLabel}
        getIcon={getIcon}
        onOpenWizard={() => setShowSmartWizard(true)}
      />
      
      {/* Modal de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* SmartWizard */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-professional"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          loadData();
          showSuccess('Profissional criado com sucesso!');
        }}
      />
    </ProfessionalsDataProvider>
  );
}

// Componente interno que tem acesso ao context
function ProfessionalsPageContent({
  professionals,
  places,
  services,
  hint,
  formDialog,
  deleteDialog,
  photoDialog,
  selectedProfessional,
  snackbar,
  showSuccess,
  hideSnackbar,
  loadData,
  handleSubmit,
  handlePhotoClick,
  handlePhotoUpload,
  handleOpenAppointmentDialog,
  handleSearchNextSlots,
  loadingNextSlots,
  getLabel,
  getIcon,
  onOpenWizard,
}) {
  const { getPlace, getService, getProfessionalStats } = useProfessionalsData();

  return (
    <Box>
      {/* Header */}
      <Box sx={pageHeader}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="primary.main">
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
            {getLabel('professionals')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLabel('professionalsDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenWizard}>
            Novo Profissional
          </Button>
        </Box>
      </Box>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={onOpenWizard} />

      {/* Empty State */}
      {professionals.length === 0 ? (
        <EmptyState
          icon={PersonIcon}
          title="Nenhum profissional cadastrado"
          message="Comece criando seu primeiro profissional para gerenciar serviços e agendamentos"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenWizard}>
              Criar Primeiro Profissional
            </Button>
          }
        />
      ) : (
        <Grid container spacing={3}>
          {professionals.map((professional) => {
            const stats = getProfessionalStats(professional);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={professional.id}>
                <Card elevation={0} sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main'
                }}>
                  {/* Header do Card com Avatar */}
                  <Box sx={{ 
                    p: 2, 
                    pb: 1, 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    minWidth: 0
                  }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <ProfessionalAvatar
                        professionalId={professional.id}
                        photoTimestamp={professional.photoTimestamp}
                        name={professional.name}
                        sx={{
                          width: { xs: 56, sm: 64 },
                          height: { xs: 56, sm: 64 },
                          bgcolor: 'primary.main',
                          fontSize: { xs: 20, sm: 24 },
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(professional.name)}
                      </ProfessionalAvatar>
                      <IconButton
                        size="small"
                        onClick={() => handlePhotoClick(professional)}
                        sx={{
                          position: 'absolute',
                          bottom: -4,
                          right: -4,
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 28,
                          height: 28,
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          },
                          boxShadow: 2
                        }}
                      >
                        <CameraIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        noWrap 
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {professional.name}
                      </Typography>
                      {professional.specialty && (
                        <Chip 
                          label={professional.specialty} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            mt: 0.5,
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <CardContent sx={{ pt: 1, pb: 1, flex: 1, minWidth: 0 }}>
                    {/* Informações de Contato */}
                    <List dense disablePadding>
                      {professional.email && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <EmailIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.email} 
                            primaryTypographyProps={{ 
                              variant: 'body2', 
                              noWrap: true,
                              sx: { overflow: 'hidden', textOverflow: 'ellipsis' }
                            }}
                          />
                        </ListItem>
                      )}
                      {professional.phone && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <PhoneIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.phone} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      )}
                      {professional.registrationNumber && (
                        <ListItem disablePadding sx={{ mb: 0.5, minWidth: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                            <BadgeIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={professional.registrationNumber} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      )}
                    </List>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Estatísticas Rápidas */}
                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <Chip 
                        icon={getIconComponent(getIcon('places').name)}
                        label={`${stats.placesCount} ${stats.placesCount === 1 ? 'Local' : 'Locais'}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip 
                        icon={getIconComponent(getIcon('services').name)}
                        label={`${stats.servicesCount} ${stats.servicesCount === 1 ? 'Serviço' : 'Serviços'}`}
                        size="small"
                        variant="outlined"
                        color={stats.servicesCount > 0 ? 'primary' : 'default'}
                      />
                    </Box>

                    {/* Serviços por Local - Sempre Expandido */}
                    <Box mt={2}>
                      <Typography {...cardSectionHeaderText} color="primary" gutterBottom>
                        Serviços por Local:
                      </Typography>
                      {professional.places.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Nenhum serviço vinculado ainda
                        </Alert>
                      ) : (
                        professional.places
                          .filter((placeLink) => !!getPlace(placeLink.placeId))
                          .map((placeLink) => {
                          const place = getPlace(placeLink.placeId);

                          return (
                            <Paper key={placeLink.id} variant="outlined" sx={{ 
                              p: 1.5, 
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold" 
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {React.createElement(getIconClass(getIcon('places').name), { fontSize: 'small', sx: { flexShrink: 0 } })}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {place.name}
                                </span>
                              </Typography>
                              <List dense disablePadding>
                                {placeLink.services.length === 0 ? (
                                  <ListItem disablePadding>
                                    <ListItemText 
                                      primary="Nenhum serviço configurado neste local"
                                      primaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: 'text.secondary',
                                        fontStyle: 'italic'
                                      }}
                                    />
                                  </ListItem>
                                ) : (
                                  placeLink.services
                                    .filter((serviceLink) => !!getService(serviceLink.serviceId))
                                    .map((serviceLink) => {
                                    const service = getService(serviceLink.serviceId);
                                    const slotKey = `${professional.id}-${serviceLink.serviceId}-${placeLink.placeId}`;
                                    const isLoadingSlots = loadingNextSlots[slotKey];

                                    return (
                                      <ListItem key={serviceLink.id} disablePadding sx={{ pl: { xs: 0, sm: 1 }, mb: 1.5 }}>
                                        <ListItemText
                                          primary={
                                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} minWidth={0}>
                                              <Box display="flex" alignItems="center" gap={0.5} minWidth={0} flex={1}>
                                                {React.createElement(getIconClass(getIcon('services').name), { fontSize: 'small', color: 'action', sx: { flexShrink: 0 } })}
                                                <Typography 
                                                  variant="body2"
                                                  sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {service.name}
                                                </Typography>
                                              </Box>
                                              <Tooltip title="Buscar próximos horários disponíveis">
                                                <Link
                                                  component="button"
                                                  variant="caption"
                                                  onClick={() => handleSearchNextSlots(
                                                    professional.id, 
                                                    serviceLink.serviceId, 
                                                    placeLink.placeId
                                                  )}
                                                  disabled={isLoadingSlots}
                                                  sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 0.5,
                                                    flexShrink: 0,
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' }
                                                  }}
                                                >
                                                  {isLoadingSlots ? (
                                                    <CircularProgress size={12} />
                                                  ) : (
                                                    <SearchIcon sx={{ fontSize: 14 }} />
                                                  )}
                                                  {isLoadingSlots ? 'Buscando...' : 'Próximos'}
                                                </Link>
                                              </Tooltip>
                                            </Box>
                                          }
                                          secondary={
                                            <Box component="span" display="flex" gap={1} mt={0.5}>
                                              {service.durationMinutes != null && service.durationMinutes !== 0 && (
                                                <Chip
                                                  size="small"
                                                  label={formatDuration(service.durationMinutes)}
                                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                              )}
                                              {service.price != null && service.price !== 0 && (
                                                <Chip
                                                  size="small"
                                                  label={formatCurrency(service.price)}
                                                  color="success"
                                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                              )}
                                            </Box>
                                          }
                                        />
                                      </ListItem>
                                    );
                                  })
                                )}
                              </List>
                            </Paper>
                          );
                        })
                      )}
                    </Box>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<ScheduleIcon />}
                      onClick={() => handleOpenAppointmentDialog(professional.id)}
                    >
                      Agendar Horário
                    </Button>
                    <Box>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => formDialog.handleOpen(professional)}
                        title="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => deleteDialog.handleOpen(professional)}
                        title="Excluir"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Professional Form Dialog */}
      <ProfessionalForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          loadData();
        }}
        deletePath={`/professionals/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Profile Photo Upload Dialog */}
      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        currentPhotoUrl={selectedProfessional?.photoUrl}
      />

    </Box>
  );
}
